import React, { memo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '../../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import { moderateScale } from '../../../utils/responsive';
import {
    SLIM_HEIGHT,
    CARD_FONTS,
    CARD_ICONS,
    CARD_PADDING,
    CARD_RADIUS,
    CARD_SHADOW
} from './cardConstants';

/**
 * DailyCard - Slim horizontal card for "Questões Aleatórias"
 * Premium dark blue gradient with white bold fonts
 * Navigates to QuestionSolver with mode 'all'
 */
const DailyCard = memo(({ onPress, isDarkMode = true }) => {
    const isLightMode = !isDarkMode;
    const cardColors = isLightMode ? ['#FFFFFF', '#F7F7FB'] : ['#1E3A5F', '#152238', '#0D1520'];

    return (
        <TouchableOpacity
            style={[styles.cardContainer, isLightMode && styles.cardContainerLight]}
            onPress={onPress}
            activeOpacity={0.9}
        >
            <LinearGradient
                colors={cardColors}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.card, isLightMode && styles.cardLight]}
            >
                <View style={styles.row}>
                    {/* Icon */}
                    <Text style={[styles.icon, isLightMode && styles.iconLight]}>∞</Text>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text numberOfLines={1} style={[styles.title, isLightMode && styles.titleLight]}>Questões Aleatórias</Text>
                        <Text style={[styles.subtitle, isLightMode && styles.subtitleLight]}>Todas as questões</Text>
                    </View>
                </View>
            </LinearGradient>
        </TouchableOpacity>
    );
});

const styles = StyleSheet.create({
    cardContainer: {
        height: SLIM_HEIGHT,
        borderRadius: CARD_RADIUS.medium,
        overflow: 'hidden',
        shadowColor: '#1E3A5F',
        ...CARD_SHADOW.small,
    },

    cardContainerLight: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 7,
        elevation: 2,
    },

    card: {
        flex: 1,
        paddingHorizontal: CARD_PADDING.horizontal,
        paddingVertical: CARD_PADDING.vertical,
        borderRadius: CARD_RADIUS.medium,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },

    cardLight: {
        borderColor: '#E9E9F2',
    },

    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },

    icon: {
        fontSize: CARD_ICONS.slim,
        // `small` (8) em vez de `medium` (12): devolve 4px ao título, que estava
        // sendo abreviado. Visualmente o ícone continua separado do texto.
        marginRight: CARD_PADDING.small,
        // Versao clara da matiz do cabecalho (210deg), p/ contrastar no card escuro.
        color: '#4A9EDB',
        fontWeight: '400',
    },

    iconLight: {
        // Card branco -> a propria cor-base #03386C.
        color: '#03386C',
        fontWeight: '700',
    },

    content: {
        flex: 1,
    },

    title: {
        // 12 em vez de `CARD_FONTS.titleSmall` (14): "Questões Aleatórias" é bem
        // mais longo que o texto anterior e, com `numberOfLines={1}`, era ABREVIADO
        // com reticências neste card estreito. Com 13 ainda não cabia.
        // A constante é COMPARTILHADA com o WorldMapCard — por isso o ajuste é
        // local, e não nela, para não encolher o outro card junto.
        // O espaço também foi ampliado reduzindo a margem do ícone (ver `icon`),
        // para não precisar diminuir mais a fonte.
        fontSize: moderateScale(12),
        // Aperta levemente o texto: ganha ~1px por caractere sem mudar o corpo.
        letterSpacing: -0.2,
        fontWeight: '800',
        color: '#FFFFFF',
    },

    titleLight: {
        color: '#111827',
    },

    subtitle: {
        fontSize: CARD_FONTS.subtitle,
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.7)',
    },

    subtitleLight: {
        color: '#7C8194',
    },

    right: {
        alignItems: 'flex-end',
    },

    cta: {
        fontSize: 12,
        fontWeight: '600',
        color: '#FFFFFF',
    },
});

export default DailyCard;
